import { ReactNode, useMemo, useState } from 'react';

import { Box, styled, useTheme } from '@mui/material';
import round from 'lodash/round';
import sum from 'lodash/sum';
import { Label, Pie, PieChart, Sector, Tooltip, TooltipProps } from 'recharts';

import {
  compactNumber,
  linearMap,
  polarToCartesian,
  useElementWidth,
} from '@cast/utils';

import { useThemeColor, useThemeColors } from '../../../../hooks';
import { SxProps } from '../../../../types';
import { getThemeColor } from '../../../../utils';
import { mapPaletteColorToDataColorToken } from '../../../../utils/mapPaletteColorToDataColorToken';
import { useIsAnimationActive } from '../_hooks/useIsAnimationActive';
import { tokens } from '../../../../theme/generated/tokens';

const StyledLabel = styled(Label)(({ theme }) => ({
  fill: tokens.theme.fg.structural.primary,
  ...theme.typography.h6,
}));

type Data = {
  value: number;
  color: string;
  borderColor?: string;
};

const spike = 5;
const APPENDIX_DATA_KEY = 'appendix';

const getLayer = (data: Data[], fillColor: string, total: number) => {
  const value = Math.min(sum(data.map((item) => item.value)), total);
  return [...data, { fill: fillColor, value: total - value }];
};

const getOverlay = (data?: Data, fillColor?: string, total = 100) => {
  if (!data) {
    return [
      {
        fill: 'transparent',
        value: total,
      },
    ];
  }
  return [
    {
      fill: 'transparent',
      value: total - data.value,
    },
    {
      fill: fillColor,
      name: APPENDIX_DATA_KEY,
      value: data.value,
    },
  ];
};

type BorderProps = {
  cx?: number;
  cy?: number;
  innerRadius?: number;
  outerRadius?: number;
  startAngle?: number;
  separatorColor: string;
};
const Border = ({
  cx = 0,
  cy = 0,
  outerRadius = 0,
  innerRadius = 0,
  startAngle = 0,
  separatorColor,
}: BorderProps) => {
  const p1 = polarToCartesian({
    cx,
    cy,
    radius: innerRadius - spike,
    angle: startAngle,
  });
  const p2 = polarToCartesian({
    cx,
    cy,
    radius: outerRadius + spike,
    angle: startAngle,
  });
  return (
    <g>
      <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={separatorColor} />
    </g>
  );
};
type CustomGaugeProps = BorderProps & { name: string };
const CustomGauge = (props: Partial<CustomGaugeProps>) => {
  if (props.name === APPENDIX_DATA_KEY) {
    return (
      <g>
        <Sector {...props} />
        <Border
          {...props}
          separatorColor={tokens.theme.border.structural.inverse}
        />
      </g>
    );
  }
  return <Sector {...props} />;
};

type Variant = 'right' | 'normal' | 'full';

const anglesByVariant: Record<
  Variant,
  { startAngle: number; endAngle: number }
> = {
  normal: {
    startAngle: 225,
    endAngle: -45,
  },
  right: {
    startAngle: 90,
    endAngle: -90,
  },
  full: {
    startAngle: 450,
    endAngle: 90,
  },
};

export type SpeedometerChartProps = {
  title?: string | number;
  outerLayer: Data[];
  outerLayerBackground?: string;
  appendix?: Data;
  borderColor?: string;
  subtitle?: ReactNode;
  pieLabelProps?: {
    sx?: SxProps;
    testId?: string;
  };
  barWidth?: number;
  tooltip?: ReactNode;
  tooltipProps?: TooltipProps<any, any>;
  variant?: Variant;
  appendixSpike?: boolean;
  total?: number;
  borderedTotal?: number;
  testId?: string;
};

/**
 * This chart is meant for legacy UI support only.
 * When developing new components, use Pie chart instead.
 */
export const SpeedometerChart = ({
  outerLayer,
  outerLayerBackground = tokens.theme.bg.structural.surface,
  appendix,
  title,
  borderColor = tokens.theme.data.blue500,
  subtitle,
  pieLabelProps,
  barWidth = 18,
  tooltip,
  variant = 'normal',
  tooltipProps = {},
  appendixSpike = true,
  total = 100,
  borderedTotal = total,
  testId,
}: SpeedometerChartProps) => {
  const theme = useTheme();
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const containerWidth = useElementWidth(ref) ?? 0;

  const [gaugeBorderColor, appendixColor] = useThemeColors(
    ['initial', 'transparent'].includes(borderColor) ? undefined : borderColor,
    appendix?.color
      ? mapPaletteColorToDataColorToken(appendix?.color, appendix?.color)
      : appendix?.color
  );

  const { startAngle, endAngle } = anglesByVariant[variant];

  const _outerLayer = useMemo(
    () =>
      outerLayer.map((item) => ({
        ...item,
        fill: getThemeColor(
          theme,
          mapPaletteColorToDataColorToken(item.color, item.color)
        ),
        stroke: getThemeColor(
          theme,
          item.borderColor
            ? mapPaletteColorToDataColorToken(
                item.borderColor,
                item.borderColor
              )
            : item.borderColor
        ),
      })),
    [outerLayer, theme]
  );

  const size = containerWidth - (!!appendix && appendixSpike ? spike : 0) * 2;
  const outerRadius = (size - 2) / 2;
  const outerInner = outerRadius - barWidth * 2;

  const outerBgData = [
    {
      value: total,
      fill: useThemeColor(
        outerLayerBackground
          ? mapPaletteColorToDataColorToken(
              outerLayerBackground,
              outerLayerBackground
            )
          : outerLayerBackground
      ),
      stroke: 'transparent',
    },
  ];
  const firstLayer = getLayer(_outerLayer, 'transparent', total);
  const overlay = getOverlay(appendix, appendixColor, total);
  const outerBorderData = [
    {
      value: borderedTotal,
      fill: 'transparent',
      stroke: gaugeBorderColor,
    },
    {
      value: total - borderedTotal,
      fill: 'transparent',
      stroke: 'transparent',
    },
  ];
  const isAnimationActive = useIsAnimationActive({
    value: [firstLayer, overlay],
  });

  const formattedTitle =
    title && typeof title === 'number'
      ? title >= 1000
        ? compactNumber(title)
        : round(title, 1)
      : title;

  const sharedPieProps = {
    isAnimationActive,
    animationBegin: isAnimationActive ? 200 : 0,
    dataKey: 'value' as const,
    paddingAngle: 0,
    cornerRadius: 3,
    stroke: 'none',
    startAngle,
    endAngle,
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        aspectRatio: '1 / 1',
        '& svg:focus, & g:focus, & path:focus': { outline: 'none' },
      }}
      data-testid={testId}
      ref={setRef}
    >
      <Box position="relative" display="flex" alignItems="center">
        <PieChart width={size} height={size}>
          {/* Outer layer background */}
          <Pie
            {...sharedPieProps}
            data={outerBgData}
            outerRadius={outerRadius}
            innerRadius={outerInner}
          />
          {/* Outer layer data */}
          <Pie
            {...sharedPieProps}
            cornerRadius={0}
            data={firstLayer}
            outerRadius={outerRadius}
            innerRadius={outerInner}
          />
          {/* Appendix overlay spanning all layers */}
          <Pie
            {...sharedPieProps}
            cornerRadius={0}
            data={overlay}
            outerRadius={outerRadius}
            innerRadius={outerInner}
            shape={CustomGauge}
          >
            {title !== undefined && (
              <StyledLabel
                width={30}
                position="center"
                data-testid={pieLabelProps?.testId || 'title'}
                sx={pieLabelProps?.sx}
              >
                {formattedTitle}
              </StyledLabel>
            )}
          </Pie>
          <Pie
            {...sharedPieProps}
            data={outerBorderData}
            outerRadius={outerRadius}
            innerRadius={outerInner}
          />
          {tooltip && (
            <Tooltip
              allowEscapeViewBox={{ x: true, y: true }}
              content={() => tooltip}
              isAnimationActive={false}
              {...tooltipProps}
            />
          )}
          {!!appendix && <Tooltip defaultIndex={1} active content={() => ''} />}
        </PieChart>
      </Box>
      {subtitle && (
        <Box
          position="absolute"
          style={{
            transform: `translateY(${linearMap({
              value: containerWidth,
              sFrom: 195,
              sTo: 260,
              dFrom: 144,
              dTo: 196,
            })}px)`,
          }}
          display="flex"
          flexDirection="column"
          alignItems="center"
          color={tokens.theme.fg.structural.secondary}
        >
          {subtitle}
        </Box>
      )}
    </Box>
  );
};
